/**
 * useTeamProfile — fetches the current user's TeamMember row (or the
 * synthetic "unrestricted" admin profile) from /api/team/me and caches
 * it via React Query. Used by `hasTool()` to gate UI elements.
 *
 * Returns `{ profile, hasTool }` for ergonomic call-sites:
 *   const { hasTool } = useTeamProfile();
 *   if (hasTool('tests.create')) ...
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../features/auth';
import { config } from '../../features/auth/config';

export interface TeamProfile {
  isTeamMember: boolean;
  unrestricted: boolean;
  scopeBranches: string[];
  scopeCohortIds: string[];
  toolsAllowed: string[];
  /** Free-text job role ("HoD — CSE", "TnP Coordinator"). Display only. */
  department?: string | null;
}

const EMPTY: TeamProfile = {
  isTeamMember: false,
  unrestricted: false,
  scopeBranches: [],
  scopeCohortIds: [],
  toolsAllowed: [],
  department: null,
};

export function useTeamProfile() {
  const { token, user } = useAuth();
  const enabled = !!token && !!user;

  const query = useQuery<TeamProfile>({
    queryKey: ['team-profile', user?.id],
    enabled,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await fetch(`${config.api.baseUrl}/api/team/me`, {
        headers: {
          'Content-Type': 'application/json',
          [config.auth.headers.frontendKey]: config.auth.headers.frontendKeyValue,
          [config.auth.headers.auth]: `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
  });

  const profile = query.data ?? EMPTY;

  // Returns true if the user has the given tool key. Admin.system passes
  // every check (server returns `unrestricted: true`); non-team users
  // who don't have a TeamMember row also pass — preserves backward
  // compat for users predating the team module.
  const hasTool = (key: string): boolean => {
    if (profile.unrestricted) return true;
    if (!profile.isTeamMember) return true;
    return profile.toolsAllowed.includes(key);
  };

  return { profile, hasTool, isLoading: query.isLoading, error: query.error };
}
